import { useEffect, useState } from 'react';
import './CmsServicesCarousel.css';

export const CmsServicesCarousel = (): JSX.Element => {
	const [activeIndex, setActiveIndex] = useState(-1);
	const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
	const listItemCount = 4;
	const updateInterval = 5000;

	const updateActiveIndex = () => {
		setActiveIndex((prevIndex) => (prevIndex + 1) % listItemCount);
	};

	useEffect(() => {
		updateActiveIndex();
		const intervalId = setInterval(updateActiveIndex, updateInterval);
		setIntervalId(intervalId);

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, []);

	const handleListItemClick = (index: number) => {
		if (intervalId) {
			clearInterval(intervalId);
		}

		const id = setInterval(updateActiveIndex, updateInterval);
		setIntervalId(id);
		setActiveIndex(index);
	};

	return (
		<div>
			<div id="carousel-wrapper">
				<ServicesList activeIndex={activeIndex} onItemClick={handleListItemClick} />
				<ImageColumn activeIndex={activeIndex} />
			</div>
		</div>
	);
};

type ServicesListProps = { activeIndex: number; onItemClick: (clickedIndex: number) => void };
const ServicesList = ({ activeIndex, onItemClick }: ServicesListProps): JSX.Element => {
	return (
		<div id="worky-services-list">
			<ListItem
				text="Ha full koll på utförda arbeten"
				iconShape="house"
				active={activeIndex === 0}
				onItemClick={onItemClick}
				index={0}
			/>
			<ListItem
				text="Enkelt att beställa nya tjänster för ditt kontor"
				iconShape="plus"
				active={activeIndex === 1}
				onItemClick={onItemClick}
				index={1}
			/>
			<ListItem
				text="Se alla event och bokade aktiviteter i kalendern"
				iconShape="calendar"
				active={activeIndex === 2}
				onItemClick={onItemClick}
				index={2}
			/>
			<ListItem
				text="Personlig chat där vi hjälper dig omgående"
				iconShape="chat"
				active={activeIndex === 3}
				onItemClick={onItemClick}
				index={3}
			/>
		</div>
	);
};

type ListItemProps = {
	text: string;
	iconShape: 'house' | 'plus' | 'calendar' | 'chat';
	active: boolean;
	index: number;
	onItemClick: (clickedIndex: number) => void;
};
const ListItem = ({ text, iconShape, active, index, onItemClick }: ListItemProps): JSX.Element => {
	let icon;
	switch (iconShape) {
		case 'house':
			icon = active ? <img src="/images/worky-house-black.svg" /> : <img src="/images/worky-house-grey.svg" />;
			break;
		case 'plus':
			icon = active ? <img src="/images/worky-plus-black.svg" /> : <img src="/images/worky-plus-grey.svg" />;
			break;
		case 'calendar':
			icon = active ? (
				<img src="/images/worky-calendar-black.svg" className="calendar-icon" />
			) : (
				<img src="/images/worky-calendar-grey.svg" className="calendar-icon" />
			);
			break;
		case 'chat':
			icon = active ? (
				<img src="/images/worky-chat-bubble-black.svg" className="chat-icon" />
			) : (
				<img src="/images/worky-chat-bubble-grey.svg" className="chat-icon" />
			);
			break;
	}

	return (
		<div className={`list-item ${active ? 'active' : ''}`} onClick={() => onItemClick(index)}>
			<div className="list-icon">{icon}</div>
			<div className="list-text">
				<h3>{text}</h3>
			</div>
			<div className="progress-line-wrapper">
				<div className="progress-line-grey" />
				<div className="progress-line-black" />
			</div>
		</div>
	);
};

type ImagesColumnProps = { activeIndex: number };
const ImageColumn = ({ activeIndex }: ImagesColumnProps): JSX.Element => {
	return (
		<div id="images-column">
			<Image imageSrc="/images/worky-house-image.png" active={activeIndex === 0} />
			<Image imageSrc="/images/worky-plus-image.png" active={activeIndex === 1} />
			<Image imageSrc="/images/worky-calendar-image.png" active={activeIndex === 2} />
			<Image imageSrc="/images/worky-chat-bubble-image.png" active={activeIndex === 3} />
		</div>
	);
};

type ImageProps = { imageSrc: string; active: boolean };
const Image = ({ imageSrc, active }: ImageProps): JSX.Element => {
	return (
		<div className={`worky-list-image ${active ? 'active' : ''}`}>
			<img src={imageSrc} />
		</div>
	);
};
